import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ExternalLink, FileText, RefreshCw, CheckCircle2, ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LevelBadge } from "@/components/Badges";
import { api, usePoll } from "@/lib/api";

function ListBlock({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
      <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-700 mb-4">{title}</h3>
      <ul className="space-y-2.5">
        {items.map((x, i) => <li key={i} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />{x}</li>)}
      </ul>
    </div>
  );
}

function Roadmap({ rm }) {
  return (
    <div className="space-y-6 fade-up">
      <div className="bg-slate-900 text-white rounded-lg p-8 flex flex-col md:flex-row gap-6 md:items-center">
        <div className="flex-1"><div className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-400 mb-2">Summary</div><p className="text-slate-200 leading-relaxed">{rm.summary}</p></div>
        {rm.fit_score != null && <div className="text-center"><div className="font-display text-5xl font-extrabold text-emerald-400" data-testid="roadmap-fit-score">{rm.fit_score}</div><div className="text-xs text-slate-400 font-mono uppercase tracking-widest">Fit score</div></div>}
      </div>
      {rm.funds_needed && (
        <div className={`rounded-lg p-5 border ${rm.funds_needed.match_required ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="roadmap-funds-needed">
          <div className="font-mono text-xs uppercase tracking-[0.18em] text-slate-600 mb-1">Money you need to move forward</div>
          <div className="font-display text-2xl font-bold text-slate-900" data-testid="roadmap-upfront-funds">{rm.funds_needed.minimum_upfront_funds || "Not listed"}</div>
          <div className="text-sm text-slate-700 mt-1">{rm.funds_needed.match_required === true ? `Matching funds required${rm.funds_needed.match_percent ? ` (about ${rm.funds_needed.match_percent}%)` : ""}.` : rm.funds_needed.match_required === false ? "No matching funds required." : "Matching requirement not listed. Check the official listing."} {rm.funds_needed.explanation}</div>
          <div className="text-xs text-slate-500 mt-2">Estimate. Confirm on the official listing before you apply.</div>
        </div>
      )}
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-4">Step-by-step plan</h2>
        <ol className="relative border-l-2 border-emerald-200 ml-3 space-y-6" data-testid="roadmap-steps">
          {(rm.steps || []).map((s, i) => (
            <li key={i} className="pl-8 relative">
              <span className="absolute -left-[15px] top-0 h-7 w-7 rounded-full bg-emerald-600 text-white text-xs font-mono grid place-items-center">{i + 1}</span>
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex justify-between gap-4 flex-wrap"><h4 className="font-semibold text-slate-900">{s.title}</h4>{s.timeframe && <span className="text-xs font-mono text-slate-500">{s.timeframe}</span>}</div>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ListBlock title="Eligibility" items={rm.eligibility} testid="roadmap-eligibility" />
        <ListBlock title="Required documents" items={rm.required_documents} testid="roadmap-documents" />
        <ListBlock title="Registrations" items={rm.registrations} testid="roadmap-registrations" />
        <ListBlock title="Winning tips" items={rm.tips} testid="roadmap-tips" />
      </div>
    </div>
  );
}

export default function GrantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const load = useCallback(() => api.get(`/grants/saved/${id}`).then((r) => setDoc(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, doc?.roadmap_status === "pending", 3000);
  const regen = async () => { await api.post(`/grants/saved/${id}/roadmap`, {}); load(); };
  const remove = async () => { await api.delete(`/grants/saved/${id}`); navigate("/dashboard/grants"); };

  if (!doc) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const g = doc.grant;
  return (
    <div>
      <Link to="/dashboard/grants" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6" data-testid="back-to-grants-link"><ArrowLeft className="h-4 w-4" />Grant Finder</Link>
      <div className="flex flex-col lg:flex-row gap-6 lg:items-start justify-between mb-10">
        <div className="max-w-3xl">
          <div className="flex gap-2 items-center"><LevelBadge level={g.level} />{doc.industry && <span className="text-xs text-slate-500">for {doc.industry}</span>}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-3" data-testid="grant-detail-title">{g.title}</h1>
          <p className="text-slate-500 mt-2">{g.agency}{g.number ? ` · ${g.number}` : ""} · Deadline: {g.deadline}</p>
          {g.description && <p className="text-slate-600 mt-4 leading-relaxed">{g.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button onClick={() => navigate(`/dashboard/proposals?grant=${id}`)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="write-proposal-for-grant-button"><FileText className="h-4 w-4 mr-2" />Write proposal</Button>
          {g.url && <a href={g.url} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="grant-official-source-button"><ExternalLink className="h-4 w-4 mr-2" />Official source</Button></a>}
          <Button variant="ghost" onClick={remove} data-testid="delete-saved-grant-button"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {doc.roadmap_status === "pending" && (
        <div className="bg-white border border-dashed border-emerald-300 rounded-lg p-10 flex items-center gap-4" data-testid="roadmap-loading">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <div><div className="font-semibold text-slate-900">Mapping steps and requirements for your industry…</div><div className="text-sm text-slate-500">Usually 30–90 seconds.</div></div>
        </div>
      )}
      {(doc.roadmap_status === "none" || doc.roadmap_status === "error") && (
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          {doc.roadmap_status === "error" && <p className="text-red-600 text-sm mb-4">The roadmap couldn't be generated.</p>}
          <Button onClick={regen} data-testid="generate-roadmap-button"><RefreshCw className="h-4 w-4 mr-2" />Generate roadmap</Button>
        </div>
      )}
      {doc.roadmap_status === "ready" && doc.roadmap && <Roadmap rm={doc.roadmap} />}
    </div>
  );
}
